/**
 * Admin "View As" context — lets admins simulate different user perspectives
 * to test the UI as a trainee, unverified user, etc.
 * 
 * This only affects UI-level gating (verification status, sidebar locks, billing).
 * It does NOT change actual database permissions or RLS — the admin is still
 * the admin in the backend; this is purely a frontend simulation.
 */
import { createContext, useContext, useState, ReactNode } from 'react';
import type { BillingStatus } from '@/hooks/useBillingStatus';

export type ViewAsPersona =
  | 'admin'
  | 'verified'
  | 'verified_free'
  | 'verified_sub'
  | 'verified_credits'
  | 'verified_website'
  | 'unverified'
  | 'pending'
  | 'trainee';

interface ViewAsOverrides {
  isVerified: boolean;
  hasLicense: boolean;
  isPending: boolean;
  hasTeamAccess: boolean;
  hideAdminUI: boolean;
  /** Billing simulation — null means use real billing data */
  billing: Omit<BillingStatus, 'loading'> | null;
}

interface AdminViewAsContextValue {
  persona: ViewAsPersona;
  setPersona: (p: ViewAsPersona) => void;
  overrides: ViewAsOverrides | null;
  isSimulating: boolean;
}

const NO_BILLING: Omit<BillingStatus, 'loading'> = {
  isUnlimited: false,
  isFreeAccount: false,
  hasSubscription: false,
  subscriptionPlanType: null,
  subscriptionPeriodEnd: null,
  creditBalance: 0,
  hasWebsiteAddon: false,
};

const PERSONA_OVERRIDES: Record<Exclude<ViewAsPersona, 'admin'>, ViewAsOverrides> = {
  verified: {
    isVerified: true,
    hasLicense: true,
    isPending: false,
    hasTeamAccess: false,
    hideAdminUI: true,
    billing: null, // use real billing data
  },
  verified_free: {
    isVerified: true,
    hasLicense: true,
    isPending: false,
    hasTeamAccess: false,
    hideAdminUI: true,
    billing: {
      ...NO_BILLING,
      isUnlimited: true,
      isFreeAccount: true,
      hasWebsiteAddon: true,
    },
  },
  verified_sub: {
    isVerified: true,
    hasLicense: true,
    isPending: false,
    hasTeamAccess: false,
    hideAdminUI: true,
    billing: {
      ...NO_BILLING,
      isUnlimited: true,
      hasSubscription: true,
      subscriptionPlanType: 'unlimited',
      subscriptionPeriodEnd: new Date(Date.now() + 30 * 86400000).toISOString(),
    },
  },
  verified_credits: {
    isVerified: true,
    hasLicense: true,
    isPending: false,
    hasTeamAccess: false,
    hideAdminUI: true,
    billing: {
      ...NO_BILLING,
      creditBalance: 7,
    },
  },
  verified_website: {
    isVerified: true,
    hasLicense: true,
    isPending: false,
    hasTeamAccess: false,
    hideAdminUI: true,
    billing: {
      ...NO_BILLING,
      hasWebsiteAddon: true,
      creditBalance: 7,
    },
  },
  unverified: {
    isVerified: false,
    hasLicense: false,
    isPending: false,
    hasTeamAccess: false,
    hideAdminUI: true,
    billing: { ...NO_BILLING },
  },
  pending: {
    isVerified: false,
    hasLicense: true,
    isPending: true,
    hasTeamAccess: false,
    hideAdminUI: true,
    billing: { ...NO_BILLING },
  },
  trainee: {
    isVerified: false,
    hasLicense: false,
    isPending: false,
    hasTeamAccess: true,
    hideAdminUI: true,
    billing: { ...NO_BILLING },
  },
};

const AdminViewAsContext = createContext<AdminViewAsContextValue>({
  persona: 'admin',
  setPersona: () => {},
  overrides: null,
  isSimulating: false,
});

export function AdminViewAsProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<ViewAsPersona>('admin');

  const overrides = persona === 'admin' ? null : PERSONA_OVERRIDES[persona];
  const isSimulating = persona !== 'admin';

  return (
    <AdminViewAsContext.Provider value={{ persona, setPersona, overrides, isSimulating }}>
      {children}
    </AdminViewAsContext.Provider>
  );
}

export function useAdminViewAs() {
  return useContext(AdminViewAsContext);
}
